import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {useLinkNavigation} from '../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {addCardStyles} from '../../../libs/shared/ui/src/styles/mixins';
import lmsHorizontalVisual from '../../assets/images/lmsHorizontalVisual.png';
import lmsHorizontalVisualThin from '../../assets/images/lmsHorizontalVisualThin.png';
import lmsVerticalVisual from '../../assets/images/lmsVerticalVisual.png';
import {LmsCardLocationsEnum, trackLmsWithLocation} from '../../settings/lms/lmsHelpers';
import {lmsFrontAcademyUrl} from '../../store/stateless/navigationActions';

/*
 * Constants.
 */

const lmsFrontAcademyText = <Trans id="lms-card-front-academy">Front Academy</Trans>;
const lmsTakeCoursesText = <Trans id="lms-card-take-courses">Take courses</Trans>;
const lmsDescription = (
  <Trans id="lms-card-description">Enroll in hands-on training to achieve your goals with Front.</Trans>
);

/*
 * Types.
 */

export enum LmsCardOrientationsEnum {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

/*
 * Props.
 */

interface StyledLmsCardProps {
  orientation: LmsCardOrientationsEnum;
  location: LmsCardLocationsEnum;
  className?: string;
  isInWidget?: boolean;
}

/*
 * Style.
 */

interface PropsWithOrientation {
  $orientation: LmsCardOrientationsEnum;
}

const StyledLmsCardDiv = styled.div<PropsWithOrientation>`
  ${addCardStyles()}

  display: flex;

  height: 100%;
  ${(p) => addLmsContainerDirectionStyles(p.$orientation)}

  overflow: hidden;
`;

function addLmsContainerDirectionStyles(direction: LmsCardOrientationsEnum) {
  if (direction === LmsCardOrientationsEnum.HORIZONTAL) {
    return css`
      flex-direction: row;
      max-height: ${90 + 90 + 10}px; // 2 * "links to academy" row height + spacing.
    `;
  }

  // Vertical
  return css`
    flex-direction: column;
    align-items: baseline;
    margin-bottom: 40px;
    height: inherit;
  `;
}

const StyledLmsImg = styled.img<PropsWithOrientation>`
  ${(p) => addLmsImgDirectionStyles(p.$orientation)}
`;

function addLmsImgDirectionStyles(direction: LmsCardOrientationsEnum) {
  if (direction === LmsCardOrientationsEnum.HORIZONTAL) {
    return css`
      height: 100%;
    `;
  }

  // Vertical
  return css`
    width: 100%;
  `;
}

interface StyledContentContainerDivProps {
  $isInWidget: boolean;
}

const StyledContentContainerDiv = styled.div<StyledContentContainerDivProps>`
  display: flex;
  flex-direction: column;
  padding: ${(p) => (p.$isInWidget ? '20px' : '16px')};
`;

const StyledTitleSpan = styled.span<PropsWithOrientation>`
  font-weight: ${fontWeights.bold};
  font-size: ${(p) =>
    p.$orientation === LmsCardOrientationsEnum.VERTICAL ? fontSizes.large : fontSizes.medium};
  line-height: ${(p) => (p.$orientation === LmsCardOrientationsEnum.VERTICAL ? '1.5' : '1.3')};
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledDescriptionParagraph = styled.p<PropsWithOrientation>`
  font-size: ${(p) =>
    p.$orientation === LmsCardOrientationsEnum.VERTICAL ? fontSizes.medium : fontSizes.verySmall};
  line-height: ${(p) => (p.$orientation === LmsCardOrientationsEnum.VERTICAL ? '1.4' : '1.5')};
  color: ${(p) => p.theme.greys.shade70};
  margin-top: 4px;
  font-weight: ${fontWeights.medium};
`;

interface StyledButtonProps extends PropsWithOrientation, StyledContentContainerDivProps {}

const StyledButton = styled(Button)<StyledButtonProps>`
  width: fit-content;
  ${(p) => addButtonDirectionStyles(p.$orientation, p.$isInWidget)}
`;

function addButtonDirectionStyles(direction: LmsCardOrientationsEnum, isInWidget: boolean) {
  if (direction === LmsCardOrientationsEnum.HORIZONTAL) {
    return css`
      margin-top: ${isInWidget ? '16px' : 'auto'};
    `;
  }

  // Vertical
  return css`
    margin-top: 17px;
  `;
}

const StyledButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

/*
 * Component.
 */

export const LmsCard: FC<StyledLmsCardProps> = ({className, orientation, isInWidget, location}) => {
  const {navigateToLink} = useLinkNavigation();

  const navigateToFrontAcademy = () => {
    navigateToLink(lmsFrontAcademyUrl);
    trackLmsWithLocation(location);
  };
  const lmsHorizontal = isInWidget ? lmsHorizontalVisualThin : lmsHorizontalVisual;
  const lmsVisual = orientation === LmsCardOrientationsEnum.VERTICAL ? lmsVerticalVisual : lmsHorizontal;

  return (
    <StyledLmsCardDiv className={className} $orientation={orientation}>
      <StyledLmsImg src={lmsVisual} $orientation={orientation} />
      <StyledContentContainerDiv $isInWidget={Boolean(isInWidget)}>
        <StyledTitleSpan $orientation={orientation}>{lmsTakeCoursesText}</StyledTitleSpan>
        <StyledDescriptionParagraph $orientation={orientation}>{lmsDescription}</StyledDescriptionParagraph>
        <StyledButton
          onClick={navigateToFrontAcademy}
          $orientation={orientation}
          $isInWidget={Boolean(isInWidget)}
        >
          {
            <StyledButtonContent>
              {lmsFrontAcademyText}
              <Icon name="externalLinkSmall" />
            </StyledButtonContent>
          }
        </StyledButton>
      </StyledContentContainerDiv>
    </StyledLmsCardDiv>
  );
};
